import React, { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { isFreeUser } from './utils/planAccess';
import ReferModal from './components/ReferModal';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
  Avatar,
  AvatarImage,
  AvatarFallback,
  Button,
} from "./components/ui/sidebar";
import {
  Home,
  Search,
  Folder,
  MessageSquare,
  CalendarDays,
  BarChart2,
  Network,
  PanelLeft,
  Settings,
  HelpCircle,
  LogOut,
  Edit3,
  MailPlus, 
} from "lucide-react";

function cn(...classes: (string | false | null | undefined)[]) {
  return classes.filter(Boolean).join(" ");
}

function FloatingSidebarTrigger() {
  const { state, isMobile, openMobile } = useSidebar();

  // ✅ On mobile: show if sidebar is closed
  // ✅ On desktop: show if sidebar is collapsed
  const shouldShow = isMobile ? !openMobile : state === "collapsed";

  if (!shouldShow) return null;

  return (
    <SidebarTrigger
      className="fixed top-4 left-4 z-[1100] h-10 w-10 rounded-md bg-white shadow-md flex items-center justify-center hover:bg-gray-100 text-gray-600"
      aria-label="Open sidebar"
    />
  );
}

export function AppLayout() {
  return (
    <SidebarProvider defaultOpen={true}>
      <AppLayoutInner />
    </SidebarProvider>
  );
}


function AppLayoutInner() {
  const { state, isMobile, openMobile } = useSidebar();
  const mainNav = [
    { title: "Dashboard", href: "/dashboard", icon: <Home className="h-5 w-5" /> },
    { title: "Explore Scenarios", href: "/scenarios", icon: <Search className="h-5 w-5" /> },
    { title: "Create A Scenario", href: "/create-scenario", icon: <Edit3 className="h-5 w-5" /> },
    { title: "My Custom Scenarios", href: "/custom-scenarios", icon: <Folder className="h-5 w-5" /> },
    { title: "Conversation History", href: "/convo", icon: <MessageSquare className="h-5 w-5" /> },
    { title: "Reports", href: "/convo?view=reports", icon: <MessageSquare className="h-5 w-5" /> },
    { title: "Analytics", href: "/analytics", icon: <BarChart2 className="h-5 w-5" /> },
    { title: "Calendar", href: "/app/calendar", icon: <CalendarDays className="h-5 w-5" />, disabled: true },
    { title: "Integrations", href: "/app/integrations", icon: <Network className="h-5 w-5" />, disabled: true },
  ];

  const userName = localStorage.getItem('firstName') || 'User Name';
  const userEmail = localStorage.getItem('email') || 'user@example.com';

  const navigate = useNavigate();
  const [settingsMenuOpen, setSettingsMenuOpen] = useState<boolean>(false);

  const toggleSettingsMenu = () => setSettingsMenuOpen(prev => !prev);
  
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isGroupAdmin, setIsGroupAdmin] = useState<boolean>(false);
  const [useGemini, setUseGemini] = useState<boolean>(false);

  // Setup for Referral Modal
  const [showReferModal, setShowReferModal] = useState(false);
  const [referralEmail, setReferralEmail] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [error, setError] = useState('');
  const [referralSent, setReferralSent] = useState(false);

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSendReferral = async () => {
    if (!isValidEmail(referralEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
  
    setError(''); // clear any prior error
  
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/subscription/send-referral`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          to: referralEmail,
          message: customMessage,
          referrerName: userName,
        }),
      });
  
      const result = await response.json();
  
      if (!response.ok) {
        throw new Error(result.error || 'Failed to send referral');
      }
  
      setReferralSent(true);
    } catch (err) {
      console.error('Error sending referral:', err);
      setError('Something went wrong. Please try again later.');
    }
  };  
  //------------------------

  const goTo = (path: string) => {
    setSettingsMenuOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    const isEthos = localStorage.getItem('isEthos') === 'true';
    localStorage.clear();
    setSettingsMenuOpen(false);
    navigate(isEthos ? '/ethos-login' : '/login');
  };

  const handleGeminiToggle = () => {
    const newValue = !useGemini;
    setUseGemini(newValue);
    localStorage.setItem('useGemini', JSON.stringify(newValue));
  };

  const menuItemStyle: React.CSSProperties = {
    padding: '8px 12px',
    cursor: 'pointer',
    borderRadius: '3px',
    fontSize: '14px',
    color: '#333',
    margin: '2px 6px',
    transition: 'background 0.2s',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  };

  useEffect(() => {
    const checkAdminAndGroupStatus = async () => {
      console.log('🔎 Checking admin and group admin status...');
  
      const storedUseGemini = localStorage.getItem('useGemini');
      if (storedUseGemini !== null) {
        setUseGemini(JSON.parse(storedUseGemini));
      }
  
      // --- Admin check ---
      try {
        const storedAdmin = localStorage.getItem('isAdmin');
        if (storedAdmin !== null) {
          setIsAdmin(storedAdmin === 'true');
        } else {
          const adminRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/is-admin`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
          });
  
          if (!adminRes.ok) throw new Error('Failed admin check');
  
          const data: { isAdmin: boolean } = await adminRes.json();
          setIsAdmin(data.isAdmin);
          localStorage.setItem('isAdmin', data.isAdmin ? 'true' : 'false');
        }
      } catch (error) {
        console.error('🚨 Admin check failed:', error);
        setIsAdmin(false);
        localStorage.setItem('isAdmin', 'false');
      }
  
      // --- Group Admin check ---
      try {
        const groupAdminRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/group-admin/group-admin-for`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        });
  
        if (!groupAdminRes.ok) throw new Error('Failed group admin check');
  
        const groupData: { groupAdminFor: string[], isEthos: boolean } = await groupAdminRes.json();
        const groupList = groupData.groupAdminFor || [];
      
        localStorage.setItem("groupAdminFor", JSON.stringify(groupList));
        localStorage.setItem("isEthos", groupData.isEthos ? 'true' : 'false');

        setIsGroupAdmin(groupList.length > 0);
      } catch (err) {
        console.error('🚨 Group admin check failed:', err);
        localStorage.setItem("groupAdminFor", "[]");
        setIsGroupAdmin(false);
      }
    };
  
    checkAdminAndGroupStatus();
  }, []);

  return (

      <div className="flex min-h-screen w-full bg-gray-100">
        {(!isMobile || openMobile) && (
          <Sidebar data-variant="inset" className="bg-white">
              <SidebarHeader className="py-4">
                <div className="flex items-center justify-between px-2">
                <div className="flex items-center gap-2">
                  <img
                    src="/proactr_logo_white.png"
                    alt="ProACTr Logo"
                    style={{ height: '40px', objectFit: 'contain' }}
                  />
                </div>
                  <SidebarTrigger
                    className="h-8 w-8 rounded-md hover:bg-gray-100 flex items-center justify-center text-gray-500"
                    aria-label="Toggle sidebar"
                  >
                    <PanelLeft className="h-5 w-5" />
                  </SidebarTrigger>
                </div>
              </SidebarHeader>
                <SidebarContent className="flex flex-col h-full justify-between">
                {/* Scrollable main content */}
                <div>
                  <SidebarGroup>
                    <SidebarGroupLabel>Navigation</SidebarGroupLabel>
                    <SidebarGroupContent>
                      <SidebarMenu>
                        {mainNav.map((item) => (
                          <SidebarMenuItem key={item.title}>
                            {item.disabled ? (
                              <div className="flex items-center gap-2 p-2 text-sm text-gray-400 cursor-not-allowed">
                                {item.icon}
                                <span className="flex-1">{item.title}</span>
                                <span className="text-[10px] uppercase tracking-wide text-gray-400 ml-auto">Coming Soon</span>
                              </div>
                            ) : (
                              <SidebarMenuButton
                                asChild
                                isActive={
                                  item.href === '/convo'
                                    ? window.location.pathname === '/convo' && !window.location.search.includes('view=reports')
                                    : item.href === '/convo?view=reports'
                                      ? window.location.pathname === '/convo' && window.location.search.includes('view=reports')
                                      : window.location.pathname === item.href
                                }
                              >
                                <a href={item.href}>
                                  {item.icon}
                                  <span>{item.title}</span>
                                </a>
                              </SidebarMenuButton>
                            )}
                          </SidebarMenuItem>
                        ))}
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </SidebarGroup>

                  <SidebarSeparator />

                  <SidebarGroup>
                    <SidebarGroupLabel>Settings</SidebarGroupLabel>
                    <SidebarGroupContent>
                      <SidebarMenu>
                        {isFreeUser() && (
                          <SidebarMenuItem>
                            <SidebarMenuButton
                              asChild
                              isActive={window.location.pathname === '/pricing'}
                              className="text-purple-700 italic font-semibold hover:text-purple-900"
                            >
                              <a href="/pricing">
                                <BarChart2 className="h-5 w-5" />
                                <span>Upgrade to Pro</span>
                              </a>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        )}
                        <SidebarMenuItem>
                          <SidebarMenuButton asChild isActive={window.location.pathname === '/profile'}>
                            <a href="/profile">
                              <Settings className="h-5 w-5" />
                              <span>Profile</span>
                            </a>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                        <SidebarMenuItem>
                          <SidebarMenuButton asChild isActive={window.location.pathname === '/help'}>
                            <a href="/help">
                              <HelpCircle className="h-5 w-5" />
                              <span>Help</span>
                            </a>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                        <SidebarMenuItem>
                          <SidebarMenuButton asChild isActive={false}>
                            <button onClick={handleLogout} className="w-full text-left flex items-center gap-2">
                              <LogOut className="h-5 w-5" />
                              <span>Logout</span>
                            </button>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </SidebarGroup>

                </div>


                {/* Gemini toggle pinned at the bottom of main content */}
                {isAdmin && (
                  <div className="px-3 py-4 border-t border-gray-200">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={useGemini}
                        onChange={handleGeminiToggle}
                        className="form-checkbox h-4 w-4 text-blue-600"
                      />
                      Use Gemini AI
                    </label>
                  </div>
                )}

                {/* Refer a friend */}
                <div className="px-3 pb-2">
                  <div
                    onClick={() => setShowReferModal(true)}
                    className="cursor-pointer bg-purple-50 hover:bg-purple-100 transition-colors duration-200 rounded-md px-4 py-3 flex items-center gap-3 shadow-sm hover:shadow-md"
                  >
                    <div className="text-purple-700">
                      <MailPlus className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-purple-800">Refer a Friend</p>
                      <p className="text-xs text-purple-600">Invite someone to try ProACTr</p>
                    </div>
                  </div>
                </div>

              </SidebarContent>
              <SidebarFooter>
                <div className="px-2">
                  <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white p-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src="/portrait-icon.png" alt="@user" />
                      <AvatarFallback>{userName?.[0]?.toUpperCase() || 'U'}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 truncate">
                      <p className="text-sm font-medium">{userName}</p>
                      <p className="truncate text-xs text-gray-500">{userEmail}</p>
                    </div>
                    <div style={{ position: 'relative' }}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full"
                      onClick={toggleSettingsMenu}
                    >
                      <Settings className="h-4 w-4" />
                      <span className="sr-only">Settings</span>
                    </Button>

                    {settingsMenuOpen && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '48px',
                          right: '0',
                          background: '#fff',
                          border: '1px solid #ccc',
                          borderRadius: '5px',
                          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                          zIndex: 1000,
                          minWidth: '140px',
                        }}
                      >
                        <ul style={{ listStyleType: 'none', margin: 0, padding: '8px 0' }}>
                          <li
                            style={menuItemStyle}
                            onClick={() => goTo('/profile')}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                          >
                            Profile
                          </li>
                          <li
                            style={menuItemStyle}
                            onClick={() => goTo('/subscription')}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                          >
                            Billing
                          </li>
                          {isAdmin && (
                            <li
                              style={menuItemStyle}
                              onClick={() => goTo('/admin')}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                              Admin Home
                            </li>
                          )}
                          {isGroupAdmin && (
                            <li
                              style={menuItemStyle}
                              onClick={() => goTo('/group-admin-dashboard')}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                              Group Admin
                            </li>
                          )}
                          <li
                            style={menuItemStyle}
                            onClick={() => goTo('/help')}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                          >
                            Help
                          </li>
                          <li
                            style={menuItemStyle}
                            onClick={handleLogout}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                          >
                            Logout
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                  </div>
                </div>
              </SidebarFooter>
          </Sidebar>
        )}
        <FloatingSidebarTrigger />

        {showReferModal && (
          <ReferModal title="Refer a Friend" onClose={() => setShowReferModal(false)}>
            {referralSent ? (
              <div className="text-center space-y-4">
                <h3 className="text-lg font-semibold text-green-600">Referral Sent!</h3>
                <p className="text-sm text-gray-700">Thanks for sharing ProACTr! Your friend will get an invitation shortly.</p>
                <button
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded"
                  onClick={() => {
                    setShowReferModal(false);
                    setReferralSent(false);
                    setReferralEmail('');
                    setCustomMessage('');
                  }}
                >
                  Close
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-700 text-sm">
                  Are you loving ProACTr? Help your friends and colleagues grow too. Invite them to try ProACTr and start improving their leadership conversations.
                </p>
                <p className="text-gray-700 text-sm italic">
                  No credit card needed – they can get started for free.
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Friend's Email</label>
                  <input
                    type="email"
                    value={referralEmail}
                    onChange={(e) => setReferralEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    placeholder="Enter your friend's email"
                  />
                  {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Optional Message</label>
                  <textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    placeholder="Write a personal message..."
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <button
                    className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded"
                    onClick={() => setShowReferModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded"
                    onClick={handleSendReferral}
                  >
                    Send Referral
                  </button>
                </div>
              </div>
            )}
          </ReferModal>
        )}

        <SidebarInset className="bg-gray-100 transition-all duration-300">
          <main className="flex-1">
            <div className="container py-6">
              <Outlet />
            </div>
          </main>
        </SidebarInset>
      </div>

  );
}
